import { AttachmentsAction } from 'aos-services/src/core/attachments/actions'
import { AttachmentsState } from 'aos-services/src/core/attachments/state'
import { AosTaskChecklistTemplate } from 'aos-services/src/services/checklists/types/AosTaskChecklistTemplate'
import { isPersistentRecurrenceTaskOrInstance } from 'aos-services/src/services/tasks/types/payload/BaseTaskFormPayload'
import { TaskFormPayload } from 'aos-services/src/services/tasks/types/payload/TaskFormPayload'
import { TaskMetadata } from 'aos-services/src/services/tasks/types/TaskMetadata'
import { TaskStatus } from 'aos-services/src/services/tasks/types/TaskStatus'
import { DialogView } from 'aos-ui/src/components/modal/MapAwareModal'
import React, { FC, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { TaskOrTemplatePayload } from '../../utils'
import { DetailedTaskForm, TaskRecurrence } from './detailed-forms'
import {
    TaskAssigneesField,
    TaskAttachmentsField,
    TaskBillableField,
    TaskCategoryField,
    TaskChecklistsField,
    TaskContractorsField,
    TaskDescriptionField,
    TaskLocationFormField,
    TaskProcessTypeField,
    TaskSchedulingField,
    TaskSeverityField,
    TaskSpendTimeField,
    TaskStatusField,
    TaskTemplateNameField,
    TaskTitleField,
} from './fields'
import { TaskFormLayout } from './TaskFormLayout'

interface TaskFormProps {
    attachmentsState: AttachmentsState
    metadata: TaskMetadata
    statusReadOnly?: boolean
    checklists: AosTaskChecklistTemplate[]

    setViewMode(n: DialogView): void
    attachmentsAction(a: AttachmentsAction): void
    isTaskTemplate: boolean
}

export const TaskForm: FC<TaskFormProps> = ({
    setViewMode,
    metadata,
    attachmentsState,
    attachmentsAction,
    statusReadOnly,
    checklists,
    isTaskTemplate,
}) => {
    const { control } = useFormContext<TaskOrTemplatePayload>()
    const task = useWatch({ control }) as TaskFormPayload
    const canEditRecurrence = useMemo(() => !task.parentTaskId, [task])
    const canSwitchRecurrence = useMemo(() => !isPersistentRecurrenceTaskOrInstance(task), [task])

    const main = (
        <>
            {/* AOS-980: the template's required identifier sits first so it is
                visible above the fold and not confused with the optional
                `Title` field further down. */}
            {isTaskTemplate && <TaskTemplateNameField />}
            <TaskSeverityField />
            <TaskProcessTypeField />
            <TaskCategoryField />
            <TaskAssigneesField />
            <DetailedTaskForm metadata={metadata} isTaskTemplate={isTaskTemplate} />
            <TaskBillableField />
            <TaskContractorsField />
            <TaskTitleField isTaskTemplate={isTaskTemplate} />
            <TaskDescriptionField isTaskTemplate={isTaskTemplate} />
            <TaskChecklistsField checklists={checklists} />
        </>
    )

    const sidebar = (
        <>
            {!isTaskTemplate && (
                <>
                    <TaskStatusField statusReadOnly={statusReadOnly} />
                    <TaskSpendTimeField />
                    {canEditRecurrence && (
                        <TaskRecurrence
                            disabled={task.status !== TaskStatus.Todo}
                            recurrenceChangeable={canSwitchRecurrence}
                        />
                    )}
                    <TaskSchedulingField />
                </>
            )}
            <TaskLocationFormField isTaskTemplate={isTaskTemplate} setViewMode={setViewMode} />
            <TaskAttachmentsField
                attachmentsState={attachmentsState}
                attachmentsAction={attachmentsAction}
            />
        </>
    )

    return <TaskFormLayout main={main} sidebar={sidebar} />
}
